<template>
  <div class="d-flex fill-height interaction-layout">
    <v-main class="d-flex justify-center align-center content py-0">
      <v-card
        data-testid="interaction-link"
        :data-state="state"
        :width="isSmall || isXSmall ? 350 : 500"
        class="pa-3 mx-auto"
      >
        <div class="card-logo-wrap">
          <img
            data-testid="link-logo"
            :src="require('./../assets/quartix-logo.png')"
            alt="Quartix.io"
            class="card-logo"
          >
        </div>

        <v-card-title class="text-h5" style="word-break: keep-all">
          {{ cardTitle }}
        </v-card-title>

        <v-card-text>
          <div
            v-if="state === 'loading'"
            class="d-flex flex-column align-center py-8"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            />
            <div class="mt-4 text-body-1">
              {{ $t('otpLink.validatingLink') }}
            </div>
          </div>

          <div v-else-if="state === 'form'">
            <p class="mb-2">
              {{ currentDescription }}
            </p>

            <p
              v-if="modeLabel"
              class="mb-4"
            >
              <span class="font-weight-bold">{{ $t('otpLink.actionLabel') }}:</span>
              {{ modeLabel }}
            </p>

            <div
              v-if="summaryItems.length"
              class="mb-4"
            >
              <v-card
                outlined
                class="pa-4"
              >
                <div
                  v-for="item in summaryItems"
                  :key="item.key"
                >
                  <span class="font-weight-bold">{{ item.label }}:</span>
                  {{ item.value }}
                </div>
              </v-card>
            </div>

            <BaseTimer
              v-if="hasExpiry"
              :timeLimit="expiresAt"
              @timeDue="onTimeDue"
            />

            <v-form
              v-if="isQuoteMode"
              ref="form"
              v-model="formValid"
              class="mt-4"
            >
              <v-text-field
                v-model="priceInput"
                data-testid="link-price"
                dense
                outlined
                maxLength="6"
                required
                :label="$t('otpLink.priceLabel')"
                :placeholder="$t('otpLink.pricePlaceholder')"
                :rules="priceRules"
                @keypress="blockNonNumericPrice"
                @input="normalizePrice"
                @keyup.enter="submitInteraction"
              />
            </v-form>

            <div
              v-else
              class="mt-4"
            >
              <v-card
                outlined
                class="px-4 py-2"
              >
                {{ $t('otpLink.confirmDescription') }}
              </v-card>
            </div>
          </div>

          <div v-else-if="state === 'confirming'" class="d-flex flex-column align-center py-6">
            <BaseTimer
              v-if="hasExpiry"
              v-show="false"
              :timeLimit="expiresAt"
              @timeDue="onTimeDue"
            />
            <p class="mb-1 text-body-1">{{ $t('otpLink.confirmingMessage') }}</p>
            <div class="text-h4 font-weight-bold my-4">{{ pendingPrice }}</div>
            <p class="text-caption mt-2 text--secondary">
              {{ $t('otpLink.confirmingCountdown', { seconds: confirmationCountdown }) }}
            </p>
            <v-progress-circular
              :value="confirmationProgress"
              color="primary"
              size="100"
              width="8"
              class="mb-3"
            >
              <span class="text-h4 font-weight-bold">{{ confirmationCountdown }}</span>
            </v-progress-circular>
          </div>

          <div
            v-else
            class="text-center d-flex flex-column align-center py-6"
          >
            <v-icon
              :color="statusColor"
              size="56"
            >
              {{ statusIcon }}
            </v-icon>

            <div class="mt-4 text-body-1">
              {{ statusMessage }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <template v-if="state === 'confirming'">
            <v-btn
              color="primary"
              outlined
              :disabled="submitting"
              @click="cancelConfirmation"
            >
              <span class="text-capitalize">{{ $t('otpLink.undo') }}</span>
            </v-btn>
          </template>

          <v-spacer></v-spacer>

          <template v-if="state === 'form'">
            <v-btn
              color="primary"
              elevation="0"
              tile
              :loading="submitting || otpLinkStore.loading"
              :disabled="disablePrimaryAction"
              data-testid="link-submit"
              @click="submitInteraction"
            >
              <span class="text-capitalize">
                {{ currentActionButton }}
              </span>
            </v-btn>
          </template>

          <template v-if="state === 'confirming'">
            <v-btn
              color="primary"
              elevation="0"
              tile
              :loading="submitting"
              data-testid="link-confirm-now"
              @click="executeQuoteSubmit"
            >
              <span class="text-capitalize">{{ $t('otpLink.confirmQuote') }}</span>
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-main>

    <TheFooter />
  </div>
</template>

<script>
import { mapActions, mapState, mapStores } from 'pinia'
import { useDisplayStore, useSnackbarStore } from '@/store/index'
import { useOtpLinkStore, INTERACTION_CONSUME_ON_LOAD } from '@/store/interactions'
import BaseTimer from '@/components/BaseTimer.vue'
import TheFooter from '@/layouts/TheFooter.vue'

const CONFIRMATION_TIMEOUT_SECS = 10 // Change this value to adjust the auto-confirm countdown duration

export default {
  name: 'InteractionLinkView',
  components: {
    BaseTimer,
    TheFooter
  },
  data() {
    return {
      state: 'loading',
      formValid: false,
      token: '',
      price:'',
      interactionType: '',
      interactionContext: {},
      expiresAt: null,
      submitting: false,
      pendingPrice: '',
      confirmationCountdown: 0,
      _confirmationInterval: null,
    }
  },
  computed: {
    ...mapState(useDisplayStore, [
      'isXSmall',
      'isSmall'
    ]),
    ...mapStores(useOtpLinkStore),
    consumeOnLoad() {
      return INTERACTION_CONSUME_ON_LOAD
    },
    isQuoteMode() {
      return this.interactionType === 'quote_response'
    },
    modeLabel() {
      if (!this.interactionType) return ''
      return this.$t(`otpLink.modes.${this.interactionType}.label`)
    },
    currentDescription() {
      if (!this.interactionType) {
        return this.$t('otpLink.genericDescription')
      }
      return this.$t(`otpLink.modes.${this.interactionType}.description`)
    },
    currentActionButton() {
      if (!this.interactionType) {
        return this.$t('otpLink.confirm')
      }
      return this.$t(`otpLink.modes.${this.interactionType}.button`)
    },
    disablePrimaryAction() {
      if (this.submitting || this.otpLinkStore.loading) return true
      if (!this.isQuoteMode) return false
      return !this.formValid
    },
    priceRules() {
      return [
        (value) => !!value || this.$t('otpLink.errors.requiredPrice'),
        (value) => /^\d+(\.\d{1,4})?$/.test(value || '') || this.$t('otpLink.errors.invalidPrice')
      ]
    },
    hasExpiry() {
      return Boolean(this.expiresAt) && (this.state === 'form' || this.state === 'confirming')
    },
    confirmationProgress() {
      return (this.confirmationCountdown / CONFIRMATION_TIMEOUT_SECS) * 100
    },
    summaryItems() {
      if (!this.interactionContext || typeof this.interactionContext !== 'object') {
        return []
      }

      return Object.keys(this.interactionContext)
        .filter((key) => {
          const value = this.interactionContext[key]
          if (value === undefined || value === null) return false
          const normalized = String(value).trim().toLowerCase()
          return normalized !== '' && normalized !== 'undefined' && normalized !== 'null' && normalized !== 'none'
        })
        .map((key) => ({
          key,
          label: key.replaceAll('_', ' '),
          value: this.interactionContext[key]
        }))
    },
    cardTitle() {
      if (this.state === 'form') {
        if (!this.interactionType) return this.$t('otpLink.title')
        return this.$t(`otpLink.modes.${this.interactionType}.title`)
      }
      if (this.state === 'loading') return this.$t('otpLink.title')
      if (this.state === 'confirming') return this.$t('otpLink.confirmingTitle')
      if (this.state === 'success') return this.$t('otpLink.successTitle')
      if (this.state === 'expired') return this.$t('otpLink.expiredTitle')
      if (this.state === 'invalid') return this.$t('otpLink.invalidTitle')
      return this.$t('otpLink.errorTitle')
    },
    statusColor() {
      if (this.state === 'success') return 'success'
      if (this.state === 'expired' || this.state === 'invalid') return 'attention'
      return 'warning'
    },
    statusIcon() {
      if (this.state === 'success') return 'mdi-check-circle'
      if (this.state === 'expired') return 'mdi-clock-alert'
      if (this.state === 'invalid') return 'mdi-link-off'
      return 'mdi-alert-circle'
    },
    statusMessage() {
      if (this.state === 'success') return this.$t('otpLink.successMessage')
      if (this.state === 'expired') return this.$t('otpLink.expiredMessage')
      if (this.state === 'invalid') return this.$t('otpLink.invalidMessage')
      return this.$t('otpLink.genericError')
    },
    priceInput:{
    get()
      {
        return this.price
      },
      set(v)
      {
        const regex = /^[0-9]?\.?[0-9]{0,4}$/;
        const raw = v.replace(/[^0-9]/g, "")
        if (raw === "")
        {
          this.price = ""
          return
        }
        const digits = raw.slice(0,5)

        if(digits.length === 1)
        {
          this.price = `${digits}`
        }
        else
        {
          const whole = digits[0]
          const decimals = digits.slice(1)
          this.price = `${whole}.${decimals}`

        }
      }
    }
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.initialize()
      }
    }
  },
  beforeDestroy() {
    this._clearConfirmationInterval()
  },
  methods: {
    ...mapActions(useOtpLinkStore, [
      'validateInteractionToken',
      'confirmInteraction',
      'submitInteractionQuote',
    ]),
    initialize() {
      this._clearConfirmationInterval()
      this.formValid = false
      this.priceInput = ''
      this.pendingPrice = ''
      this.confirmationCountdown = 0
      this.interactionType = ''
      this.interactionContext = {}
      this.expiresAt = null

      const token = this.resolveToken()
      if (!token) {
        this.state = 'invalid'
        return
      }

      this.token = token
      this.state = 'loading'

      this.validateInteractionToken(token)
        .then((response) => {
          const data = response?.data || {}
          if (data.status === 'expired') {
            this.state = 'expired'
            return
          }

          this.interactionType = this.normalizeInteractionType(
            data.interaction_type || data.action_type || data.action || data.type || data.status
          )
          if (!this.interactionType) {
            this.state = 'invalid'
            return
          }

          this.interactionContext = data.context || data.payload || {}
          this.expiresAt = this.toEpochMs(data.expires_at || data.expiration || data.expiresAt)
          this.state = 'form'

          if (this.consumeOnLoad && this.interactionType !== 'quote_response') {
            this.submitInteraction({ silentToast: true })
          }
        })
        .catch((error) => {
          this.state = this.stateFromStatus(error?.response?.status)
        })
    },
    resolveToken() {
      return this.$route.query.token || this.$route.params.token
    },
    normalizeInteractionType (rawType) {
      const normalized = String(rawType || '').toLowerCase()
      if (!normalized) return ''

      const aliases = {
        operation_approval: 'operation_approval',
        approval: 'operation_approval',
        new_operation_approval: 'operation_approval',
        quote_response: 'quote_response',
        quote: 'quote_response',
        price_form: 'quote_response',
        aprovado: 'quote_response'
      }

      return aliases[normalized] || ''
    },
    stateFromStatus (statusCode) {
      if (statusCode === 410) return 'expired'
      if (statusCode === 400 || statusCode === 404 || statusCode === 409) return 'invalid'
      return 'error'
    },
    toEpochMs (rawValue) {
      if (!rawValue) return null
      if (typeof rawValue === 'number') {
        return rawValue < 1000000000000 ? rawValue * 1000 : rawValue
      }

      const parsed = Date.parse(rawValue)
      return Number.isNaN(parsed) ? null : parsed
    },
    blockNonNumericPrice (event) {
      const allowed = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        '.'
      ]

      if (allowed.includes(event.key)) return
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    normalizePrice (value) {
      const rawValue = String(value || '')
      const onlyAllowed = rawValue.replace(/[^0-9.]/g, '')
      const firstDotIndex = onlyAllowed.indexOf('.')

      if (firstDotIndex === -1) {
        this.priceInput = onlyAllowed
        return
      }

      const intPart = onlyAllowed.slice(0, firstDotIndex)
      const decimalPart = onlyAllowed
        .slice(firstDotIndex + 1)
        .replace(/\./g, '')
        .slice(0, 4)

      this.priceInput = `${intPart || '0'}.${decimalPart}`
    },
    submitInteraction({ silentToast = false } = {}) {
      if (this.state !== 'form') return

      if (this.isQuoteMode) {
        this.$refs.form.validate()
        if (!this.formValid) return
        this.enterConfirmingStage()
        return
      }

      this.submitting = true
      this.confirmInteraction({ token: this.token })
        .then(() => {
          if (!silentToast) {
            useSnackbarStore().open(this.$t('otpLink.toasts.confirmSuccess'), 'success')
          }
          this.state = 'success'
        })
        .catch((error) => {
          this.state = this.stateFromStatus(error?.response?.status)
          useSnackbarStore().open(this.statusMessage, 'attention')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    enterConfirmingStage() {
      this.pendingPrice = this.priceInput
      this.confirmationCountdown = CONFIRMATION_TIMEOUT_SECS
      this.state = 'confirming'
      this._confirmationInterval = setInterval(() => {
        this.confirmationCountdown -= 1
        if (this.confirmationCountdown <= 0) {
          this.executeQuoteSubmit()
        }
      }, 1000)
    },
    _clearConfirmationInterval() {
      if (this._confirmationInterval !== null) {
        clearInterval(this._confirmationInterval)
        this._confirmationInterval = null
      }
    },
    cancelConfirmation() {
      this._clearConfirmationInterval()
      this.priceInput = this.pendingPrice
      this.state = 'form'
    },
    executeQuoteSubmit() {
      this._clearConfirmationInterval()
      this.submitting = true
      this.submitInteractionQuote({
        token: this.token,
        price: Number(this.pendingPrice)
      })
        .then(() => {
          useSnackbarStore().open(this.$t('otpLink.toasts.quoteSuccess'), 'success')
          this.state = 'success'
        })
        .catch((error) => {
          this.state = this.stateFromStatus(error?.response?.status)
          useSnackbarStore().open(this.statusMessage, 'attention')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    onTimeDue() {
      this._clearConfirmationInterval()
      this.state = 'expired'
    }
  }
}
</script>

<style scoped>
.interaction-layout {
  position: relative;
}

.content {
  background-image: url('./../assets/background-login.svg');
  background-repeat: repeat;
  background-color: #000;
  flex: 1 0 auto;
}

.card-logo-wrap {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.card-logo {
  width: 100%;
  max-width: 180px;
}
</style>
